import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button } from '@mui/material';
import { X } from 'lucide-react';
import axiosInstance from '../api/axiosInstance';
import { compressImage, compressVideo } from '../utils/mediaCompressor';
import { Capacitor } from '@capacitor/core';
import getCameraPlugin from '../utils/cameraPlugin';
import { useNavigate } from 'react-router-dom';
import { setPostExistingTripItems } from '../store/postExistingTripStore';

export default function PostExistingTripDialog({ open, onClose }) {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [gpsList, setGpsList] = useState([]);
  const [processingStatus, setProcessingStatus] = useState([]);
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const platform = Capacitor.getPlatform();

  const canNext = useMemo(() => {
    const hasGps = gpsList.some(Boolean);
    return files.length > 0 && hasGps;
  }, [files.length, gpsList]);

  const reset = () => {
    try { previews.forEach(u => { try { URL.revokeObjectURL(u); } catch(_){} }); } catch(_){ }
    setFiles([]);
    setPreviews([]);
    setGpsList([]);
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const tryConvertHeic = async (file) => {
    try {
      const isHeic = /\.(heic|heif)$/i.test(file.name) || /image\/hei(c|f)/i.test(file.type);
      if (!isHeic) return file;
      const heic2any = (await import('heic2any')).default;
      const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
      const blob = Array.isArray(out) ? out[0] : out;
      return new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
    } catch (e) {
      console.warn('HEIC convert failed; preview may not show', e);
      return file;
    }
  };

  const extractGps = async (file) => {
    try {
      const exifr = (await import('exifr')).default;
      let data = await exifr.gps(file);
      if (data && typeof data.latitude === 'number' && typeof data.longitude === 'number') {
        alert('[GPS] fast: ' + JSON.stringify(data));
        try { alert('[GPS fast] lat=' + data.latitude + ' lon=' + data.longitude); } catch(_) {}
        return { lat: data.latitude, lon: data.longitude, source: 'exif' };
      }
      data = await exifr.parse(file).catch(() => null);
      alert('[GPS] full: ' + JSON.stringify(data));
      try { alert('[GPS full parse] keys=' + (data ? Object.keys(data).filter(k => /lat|lon|GPS/i.test(k)).join(',') : 'none')); } catch(_) {}
      if (data) {
        const lat = data.latitude || data.lat || data.GPSLatitude;
        const lon = data.longitude || data.lon || data.GPSLongitude;
        if (typeof lat === 'number' && typeof lon === 'number') {
          try { alert('[GPS derived] lat=' + lat + ' lon=' + lon); } catch(_) {}
          return { lat, lon, source: 'exif' };
        }
      }
    } catch (e) {
      console.debug('[GPS] error', e);
      try { alert('[GPS error] ' + (e?.message || 'unknown')); } catch(_) {}
    }
    return null;
  };

  const addFiles = async (newFiles, providedGpsList = []) => {
    if (!Array.isArray(newFiles) || newFiles.length === 0) return;
    // Immediately create previews (optimistic) without blocking on HEIC/exif parsing.
    const immediateUrls = newFiles.map(f => URL.createObjectURL(f));
    setFiles(prev => [...prev, ...newFiles]);
    setPreviews(prev => [...prev, ...immediateUrls]);
    // GPS placeholders (null until resolved). If plugin provided, set immediately.
    const initialGps = newFiles.map((_, idx) => {
      const provided = providedGpsList[idx];
      return (provided && typeof provided.lat === 'number' && typeof provided.lon === 'number')
        ? { lat: provided.lat, lon: provided.lon, source: provided.source || 'plugin' }
        : null;
    });
    setGpsList(prev => [...prev, ...initialGps]);
    setProcessingStatus(prev => [...prev, ...newFiles.map(() => 'processing')]);

    // Background processing for each file (HEIC convert + EXIF GPS). Defer with setTimeout to allow paint.
    newFiles.forEach((file, localIdx) => {
      const globalIdx = files.length + localIdx; // index after state append
      // Use micro-task deferral (0ms) to not block UI thread.
      setTimeout(() => processFileInBackground(file, globalIdx, providedGpsList[localIdx]), 0);
    });
  };

  const processFileInBackground = async (origFile, idx, providedGps) => {
    let workingFile = origFile;
    let gpsValue = providedGps && typeof providedGps.lat === 'number' && typeof providedGps.lon === 'number'
      ? { lat: providedGps.lat, lon: providedGps.lon, source: providedGps.source || 'plugin' }
      : null;
    // HEIC conversion (if needed) AFTER initial preview
    try {
      if (!gpsValue && workingFile.type?.startsWith?.('image/')) {
        // Attempt EXIF GPS first on original (cheaper than conversion)
        try {
          gpsValue = await extractGps(workingFile);
        } catch (_) {}
      }
      // Convert HEIC after EXIF attempt so we don't pay conversion cost just to read EXIF
      if (/\.(heic|heif)$/i.test(workingFile.name) || /image\/hei(c|f)/i.test(workingFile.type)) {
        try {
          const heic2any = (await import('heic2any')).default;
          const out = await heic2any({ blob: workingFile, toType: 'image/jpeg', quality: 0.9 });
          const blob = Array.isArray(out) ? out[0] : out;
          const converted = new File([blob], workingFile.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
          // Replace preview URL
          setFiles(prev => prev.map((f, i) => i === idx ? converted : f));
          setPreviews(prev => prev.map((u, i) => {
            if (i !== idx) return u;
            try { URL.revokeObjectURL(u); } catch(_){}
            return URL.createObjectURL(converted);
          }));
          workingFile = converted;
          // Try EXIF again on converted if not already found
          if (!gpsValue) {
            try {
              gpsValue = await extractGps(workingFile);
            } catch (_) {}
          }
        } catch (e) {
          // Ignore conversion errors; keep original file & preview
          console.warn('HEIC conversion failed (background)', e);
        }
      }
    } finally {
      if (gpsValue) {
        setGpsList(prev => prev.map((g, i) => i === idx ? gpsValue : g));
      }
      setProcessingStatus(prev => prev.map((p, i) => i === idx ? 'done' : p));
    }
  };

  const onSelectFromInput = async (fileList) => {
    const arr = Array.from(fileList || []);
    await addFiles(arr);
  };

  const selectFromNativeGallery = async () => {
    try {
      const CameraPlugin = getCameraPlugin();
      if (platform === 'ios' && CameraPlugin?.openGallery) {
        const result = await CameraPlugin.openGallery();
        if (!result || !result.fileUri) return;
        const isVideo = !!result.isVideo;
        if (isVideo) {
          alert('Videos are not supported in this flow. Please select photos.');
          return;
        }
        const uri = result.fileUri;
        const fetchable = uri.startsWith('data:') ? uri : Capacitor.convertFileSrc(uri);
        const resp = await fetch(fetchable);
        const blob = await resp.blob();
        const mimeType = blob.type || 'image/jpeg';
        const name = `photo_${Date.now()}.jpg`;
        const file = new File([blob], name, { type: mimeType });
        // Prefer GPS provided by native plugin if available
        let gps = (typeof result.latitude === 'number' && typeof result.longitude === 'number')
          ? { lat: result.latitude, lon: result.longitude, source: 'plugin' }
          : null;
        if (gps) {
          try { alert('[Plugin GPS] lat=' + gps.lat + ' lon=' + gps.lon); } catch(_) {}
        }
        // Fallback to EXIF extraction only if plugin didn't supply coordinates
        if (!gps) {
          const exifGps = await extractGps(file);
          if (exifGps) {
            try { alert('[Using EXIF GPS]'); } catch(_) {}
            gps = exifGps;
          }
        }
        await addFiles([file], gps ? [gps] : []);
      }
      else {
        alert('Only iOS is supported in this flow.');
      }
    } catch (e) {
      console.error('selectFromNativeGallery failed', e);
      alert('Failed to pick from native gallery.');
    }
  };

  const closeIfPossible = () => {
    reset();
    onClose?.();
  };

  const buildStep2 = async () => {
    const items = [];
    const coordToRg = new Map();

    for (let i = 0; i < files.length; i++) {
      const gps = gpsList[i];
      if (!gps) continue;
      const f = files[i];
      const url = previews[i];
      const type = f.type.startsWith('video/') ? 'video' : 'image';
      const key = `${gps.lat.toFixed(5)},${gps.lon.toFixed(5)}`;
      if (!coordToRg.has(key)) {
        try {
          const { data } = await axiosInstance.post('/reverse-geocode/initiate', { latitude: gps.lat, longitude: gps.lon });
          coordToRg.set(key, data);
        } catch (e) {
          coordToRg.set(key, null);
        }
      }
      items.push({ file: f, previewUrl: url, type, lat: gps.lat, lon: gps.lon, rg: coordToRg.get(key), caption: '', price: '', rating: 0, croppedBlob: null });
    }
    setPostExistingTripItems(items);
    onClose?.();
    navigate('/post-existing-trip/details');
  };

  return (
    <Dialog
      open={open}
      onClose={closeIfPossible}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            bgcolor: isDark ? 'var(--small-dark-card)' : '#fff',
            color: isDark ? '#fff' : '#000',
            borderRadius: 3,
            border: isDark ? '1px solid rgba(255,255,255,0.08)' : 'none',
          },
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem', textAlign: 'center', pb: '30px' }}>
        Post Existing Trippin
      </DialogTitle>
      <DialogContent>
        <div className="tailwind flex flex-col gap-4">
            {platform !== 'ios' && (
              <label className="flex flex-col items-center justify-center gap-2 rounded-xl py-4 cursor-pointer transition border-2 border-dashed !border-mainLightColor">
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={(e) => onSelectFromInput(e.target.files)}
                  className="hidden"
                />
                <span className="text-base font-medium">Select media</span>
              </label>
            )}
            {platform === 'ios' && (
              <div
                role="button"
                tabIndex={0}
              onClick={() => { selectFromNativeGallery(); }}
              onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); selectFromNativeGallery(); } }}
                className={`flex flex-col items-center justify-center gap-2 rounded-xl py-4 transition border-2 border-dashed !border-mainLightColor`}
              >
                <span className="text-base font-medium">Select from gallery</span>
              </div>
            )}
            {files.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mt-2">
                {files.map((f, idx) => (
                  <div
                    key={idx}
                    className={`group relative w-full overflow-hidden rounded-lg`} 
                    style={{ aspectRatio: '3 / 4' }}
                  >
                    {processingStatus[idx] === 'done' && (
                      f.type.startsWith('video/') ? (
                        <video src={previews[idx]} className="w-full h-full object-cover" muted playsInline loop />
                      ) : (
                        <img src={previews[idx] || ''} alt={`p-${idx}`} className="object-cover w-full h-full" />
                      )
                    )}
                    {processingStatus[idx] === 'done' && (
                      <button
                        type="button"
                        aria-label="Remove media"
                        className="absolute top-1 right-1 flex items-center justify-center bg-likeColor text-white w-5 h-5 rounded-full transition focus:outline-none"
                        onClick={() => {
                          setFiles(prev => prev.filter((_, i) => i !== idx));
                          setPreviews(prev => { try { URL.revokeObjectURL(prev[idx]); } catch(_){} return prev.filter((_, i) => i !== idx); });
                          setGpsList(prev => prev.filter((_, i) => i !== idx));
                          setProcessingStatus(prev => prev.filter((_, i) => i !== idx));
                        }}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                    {processingStatus[idx] === 'processing' && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="absolute inset-0 animate-pulse bg-gray-200 dark:bg-neutral-800" />
                      </div>
                    )}
                    {processingStatus[idx] === 'done' && !gpsList[idx] && (
                      <div className="absolute bottom-1 left-1 right-1 text-[10px] bg-black/40 backdrop-blur-sm text-white px-1 py-0.5 rounded flex items-center justify-center">No GPS</div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {files.length > 0 && (
              <div className="text-xs opacity-60">Only media with GPS will proceed to the next step.</div>
            )}
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, bgcolor: isDark ? 'var(--small-dark-card)' : undefined }}>
        <Button
          onClick={closeIfPossible}
          sx={{
            textTransform: 'none',
            color: isDark ? '#fff' : '#000',
          }}
        >
          Cancel
        </Button>
        <Button
          disabled={!canNext}
          variant="contained"
          onClick={buildStep2}
          sx={{
            textTransform: 'none',
            bgcolor: 'var(--main-light-color)',
            color: 'var(--small-dark-card)',
            '&.Mui-disabled': {
              bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb',
              color: isDark ? 'rgba(255,255,255,0.4)' : '#000',
            },
          }}
        >
          Next
        </Button>
      </DialogActions>
    </Dialog>
  );
}
